const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://munca-la-schimb-production.up.railway.app/api';

// Import material types
import type { MaterialType, MaterialUnit, MaterialConsumption, MaterialAllocation, ActionLog } from '../types/index';
import type { MaterialWasteReport } from '../types/material';

// API response types matching backend DTOs
export interface ApiUser {
  id: number;
  name: string;
  role: string;
  workerType?: string;
  email?: string;
  teamId?: number;
  supervisorId?: number;
  monitorId?: number;
  // Work status fields
  isWorking?: boolean;
  onBreak?: boolean;
  currentWorkEntryId?: number | null;
  currentBreakId?: number | null;
  lastStatusUpdate?: string | null;
}

export interface ApiTeam {
  id: number;
  name: string;
  supervisorId: number;
  monitorId: number;
  members: ApiUser[];
  createdAt: string;
  updatedAt: string;
}

export interface ApiProject {
  id: number;
  name: string;
}

export interface ApiLocation {
  id: number;
  name: string;
}

export interface ApiWorkEntry {
  id: number;
  userId: number;
  userName: string;
  projectId: number;
  projectName: string;
  locationId: number;
  locationName: string;
  startTime: string;
  endTime?: string;
  breakMinutes: number;
  comments?: string;
  workDescription?: string;
  voiceRecording?: string;
  date: string;
  workDuration?: string;
  isActive: boolean;
  totalBreakMinutes: number;
  breaks: ApiBreak[];
  activeBreak?: ApiBreak;
}

export interface ApiBreak {
  id: number;
  workEntryId: number;
  startTime: string;
  endTime?: string;
  reason?: string;
  duration?: string;
  isActive: boolean;
}

export interface LoginRequest {
  name: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  user?: ApiUser;
}

export interface EndOfDayReportResult {
  success: boolean;
  message: string;
  processedEntries: WorkEntry[];
  affectedUsersCount: number;
  affectedUsers: string[];
  reportGeneratedAt: string;
  date: string;
}

export interface SupervisorEndOfDayReportResult {
  success: boolean;
  message: string;
  reportId: number;
  fileName: string;
  teamMembersSetOffline: string[];
  activeWorkEntriesEnded: number;
  date: string;
  createdAt: string;
}

export interface EndOfDayReportSummary {
  id: number;
  date: string;
  createdAt: string;
  supervisorName: string;
  supervisorId: number;
  notes?: string;
  excelFileName?: string;
  isTeamSetOffline: boolean;
  teamOfflineTimestamp?: string;
  hasExcelFile: boolean;
}

export interface CreateUserRequest {
  name: string;
  role: string;
  workerType?: string;
  email?: string;
  teamId?: number;
  supervisorId?: number;
  monitorId?: number;
  assignedLocationId?: number;
  assignedProjectId?: number;
}

export interface CreateTeamRequest {
  name: string;
  supervisorId: number;
  monitorId: number;
}

export interface UpdateTeamRequest {
  name?: string;
  supervisorId?: number;
  monitorId?: number;
}

export interface CreateWorkEntryRequest {
  userId: number;
  projectId: number;
  locationId: number;
  startTime: string;
  date: string;
}

export interface UpdateWorkEntryRequest {
  endTime?: string;
  breakMinutes: number;
  comments?: string;
  workDescription?: string;
  voiceRecording?: string;
}

export interface AddCommentRequest {
  comment: string;
}

export interface CreateBreakRequest {
  workEntryId: number;
  startTime: string;
  reason?: string;
}

export interface UpdateBreakRequest {
  endTime?: string;
  reason?: string;
}

// New optimized API types
export interface PagedResult<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface BulkWorkEntriesResult {
  workEntries: ApiWorkEntry[];
  date: string;
  userIds: number[];
}

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
}

// Bulk import interfaces
export interface BulkImportUserData {
  name: string;
  workerType: 'Angajat' | 'Colaborator';
}

export interface BulkImportResult {
  success: boolean;
  createdUsers: Array<{
    name: string;
    workerType: string;
    password: string;
  }>;
  errors: Array<{
    name: string;
    error: string;
  }>;
}

export interface BulkGroupCreateRequest {
  names: string[];
  role: 'worker' | 'supervisor' | 'monitor';
  workerType?: 'Angajat' | 'Colaborator';
  password: string;
}

export interface BulkGroupCreateResult {
  success: boolean;
  createdUsers: Array<{
    name: string;
    role: string;
    workerType?: string;
    password: string;
  }>;
  errors: Array<{
    name: string;
    error: string;
  }>;
}

export interface RegeneratedPasswordDto {
  name: string;
  role: string;
  newPassword: string;
}

export interface RegeneratePasswordsResult {
  success: boolean;
  message: string;
  regeneratedUsers: RegeneratedPasswordDto[];
}

export interface BulkOperationResult {
  success: boolean;
  successCount: number;
  errorCount: number;
  message: string;
  results: Array<{
    userId?: number;
    workEntryId?: number;
    success: boolean;
    error?: string;
  }>;
}

export interface WorkerStatus {
  userId: number;
  isWorking: boolean;
  workEntryId?: number;
  projectId?: number;
  projectName?: string;
  locationId?: number;
  locationName?: string;
  startTime?: string;
  onBreak: boolean;
  breakId?: number;
  breakStartTime?: string;
}

export interface BulkStatusResult {
  successful: WorkerStatus[];
  failed: Array<{
    userId: number;
    reason: string;
  }>;
}

// Backend response (supports both PascalCase and camelCase)
export interface BackendBulkOperationResult {
  Success?: boolean;
  SuccessCount?: number;
  ErrorCount?: number;
  Message?: string;
  Results?: Array<{
    UserId?: number;
    WorkEntryId?: number;
    Success?: boolean;
    Error?: string;
    userId?: number;
    workEntryId?: number;
    success?: boolean;
    error?: string;
  }>;
  success?: boolean;
  successCount?: number;
  errorCount?: number;
  message?: string;
  results?: Array<{
    UserId?: number;
    WorkEntryId?: number;
    Success?: boolean;
    Error?: string;
    userId?: number;
    workEntryId?: number;
    success?: boolean;
    error?: string;
  }>;
}

export interface EndOfDayReportResult {
  success: boolean;
  message: string;
  processedEntries: Array<{
    userId: number;
    userName: string;
    projectName: string;
    locationName: string;
    startTime: string;
    endTime: string;
    lunchBreakAdded: boolean;
    totalBreakMinutes: number;
  }>;
  affectedUsersCount: number;
  affectedUsers: string[];
  reportGeneratedAt: string;
  date: string;
}

class ApiService {
  private currentUserId: number | null = null;
  private useOptimizedEndpoints: boolean | null = null; // null = not tested yet
  private optimizedEndpointCheckPromise: Promise<boolean> | null = null;

  setCurrentUser(userId: number | null) {
    this.currentUserId = userId;
  }

  private async checkOptimizedEndpointsAvailable(): Promise<boolean> {
    // If already checking, wait for the result
    if (this.optimizedEndpointCheckPromise) {
      return this.optimizedEndpointCheckPromise;
    }

    // If already tested, return the cached result
    if (this.useOptimizedEndpoints !== null) {
      return this.useOptimizedEndpoints;
    }

    // Start the check
    this.optimizedEndpointCheckPromise = (async () => {
      try {
        // Try to access a simple optimized endpoint
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://localhost:7000/api';
        const response = await fetch(`${baseUrl}/optimized/health`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(this.currentUserId ? { 'X-Current-User-Id': this.currentUserId.toString() } : {})
          }
        });
        
        // If we get 404, optimized endpoints are not available
        if (response.status === 404) {
          console.log('Optimized endpoints not available in this environment, using regular endpoints');
          this.useOptimizedEndpoints = false;
          return false;
        }
        
        // Any other response (200, 401, etc) means the endpoint exists
        this.useOptimizedEndpoints = true;
        return true;
      } catch (error) {
        // Network error or other issue - assume optimized endpoints are not available
        console.log('Could not check optimized endpoints availability, using regular endpoints');
        this.useOptimizedEndpoints = false;
        return false;
      } finally {
        // Clear the promise so future calls use the cached result
        this.optimizedEndpointCheckPromise = null;
      }
    })();

    return this.optimizedEndpointCheckPromise;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retries = 2
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    console.log(`API Request: ${options.method || 'GET'} ${url}`);
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(this.currentUserId && { 'X-Current-User-Id': this.currentUserId.toString() }),
        ...options.headers,
      },
      timeout: 10000, // 10 second timeout
      ...options,
    };
    
    console.log('Request config:', config);

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch(url, config);
        
        if (!response.ok) {
          const errorText = await response.text();
          let errorDetails = null;
          
          // Try to parse error as JSON for detailed debugging info
          try {
            errorDetails = JSON.parse(errorText);
            console.error('Detailed error from server:', errorDetails);
          } catch {
            // Not JSON, use as plain text
          }
          
          const errorMessage = `HTTP ${response.status}: ${response.statusText}${errorText ? ` - ${errorText}` : ''}`;
          
          // Don't retry on client errors (4xx), only server errors (5xx) and network errors
          if (response.status >= 400 && response.status < 500) {
            const error = new Error(errorMessage);
            (error as Error & { details?: unknown }).details = errorDetails;
            throw error;
          }
          
          // Retry on server errors if we have attempts left
          if (attempt < retries) {
            console.warn(`API request failed (attempt ${attempt + 1}/${retries + 1}):`, errorMessage);
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000)); // Exponential backoff
            continue;
          }
          
          const error = new Error(errorMessage);
          (error as Error & { details?: unknown }).details = errorDetails;
          throw error;
        }
        
        // Check if response has content before parsing JSON
        const contentType = response.headers.get('content-type');
        const hasJsonContent = contentType && contentType.includes('application/json');
        const hasContent = response.status !== 204 && response.status !== 205;
        
        // Check if response body is empty
        const text = await response.text();
        if (text.trim() === '') {
          return {} as T;
        }
        
        if (hasJsonContent || hasContent) {
          try {
            return JSON.parse(text);
          } catch (jsonError) {
            console.warn('Failed to parse JSON, returning empty object:', text);
            return {} as T;
          }
        } else {
          return {} as T;
        }
      } catch (error) {
        // Network errors, timeouts, etc.
        if (attempt < retries && (error instanceof TypeError || error.message.includes('fetch'))) {
          console.warn(`Network error (attempt ${attempt + 1}/${retries + 1}):`, error.message);
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000)); // Exponential backoff
          continue;
        }
        
        // Don't log 404 errors for optimized endpoints (they fallback gracefully)
        const is404 = error.message?.includes('404') || error.message?.includes('HTTP 404');
        const isOptimizedEndpoint = url.includes('/optimized/');
        
        if (!is404 || !isOptimizedEndpoint) {
          console.error('API request failed:', error);
        }
        
        throw error;
      }
    }

    throw new Error('All retry attempts failed');
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async createUser(userData: CreateUserRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/create-user', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(userId: number, userData: Partial<User>): Promise<User> {
    return this.request<User>(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async bulkImportUsers(users: BulkImportUserData[]): Promise<BulkImportResult> {
    return this.request<BulkImportResult>('/auth/bulk-import', {
      method: 'POST',
      body: JSON.stringify({ users }),
    });
  }

  async bulkCreateGroupUsers(groupData: BulkGroupCreateRequest): Promise<BulkGroupCreateResult> {
    return this.request<BulkGroupCreateResult>('/auth/bulk-group-create', {
      method: 'POST',
      body: JSON.stringify(groupData),
    });
  }

  async regenerateSupervisorMonitorPasswords(): Promise<RegeneratePasswordsResult> {
    return this.request<RegeneratePasswordsResult>('/auth/regenerate-supervisor-monitor-passwords', {
      method: 'POST',
    });
  }

  // Users - Real-time data (no caching for live updates)
  async getAllUsers(): Promise<ApiUser[]> {
    return this.request<ApiUser[]>('/users', {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  async getUser(userId: number): Promise<ApiUser> {
    return this.request<ApiUser>(`/users/${userId}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  async resetPassword(userId: number): Promise<AuthResponse> {
    return this.request<AuthResponse>(`/auth/reset-password/${userId}`, {
      method: 'POST',
    });
  }

  async deleteUser(userId: number): Promise<void> {
    return this.request<void>(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Projects - Real-time data (no caching)
  async getProjects(): Promise<ApiProject[]> {
    return this.request<ApiProject[]>('/projects', {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  async createProject(data: { name: string; description?: string; isActive?: boolean }): Promise<ApiProject> {
    // Backend only accepts name
    return this.request<ApiProject>('/projects', {
      method: 'POST',
      body: JSON.stringify({ name: data.name }),
    });
  }

  async updateProject(id: number, data: { name: string; description?: string; isActive?: boolean }): Promise<void> {
    // Backend only accepts name
    return this.request<void>(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name: data.name }),
    });
  }

  async deleteProject(id: number): Promise<void> {
    return this.request<void>(`/projects/${id}`, {
      method: 'DELETE',
    });
  }

  // Locations - Real-time data (no caching)
  async getLocations(): Promise<ApiLocation[]> {
    return this.request<ApiLocation[]>('/locations', {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  async createLocation(data: { name: string; address?: string; projectId?: number | null }): Promise<ApiLocation> {
    // Backend only accepts name
    return this.request<ApiLocation>('/locations', {
      method: 'POST',
      body: JSON.stringify({ name: data.name }),
    });
  }

  async updateLocation(id: number, data: { name: string; address?: string; projectId?: number | null }): Promise<void> {
    // Backend only accepts name
    return this.request<void>(`/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name: data.name }),
    });
  }

  async deleteLocation(id: number): Promise<void> {
    return this.request<void>(`/locations/${id}`, {
      method: 'DELETE',
    });
  }

  // Work Entries - Real-time data (no caching for live updates)
  async getWorkEntries(
    date?: string, 
    userId?: number,
    pagination?: PaginationOptions
  ): Promise<PagedResult<ApiWorkEntry>> {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (userId) params.append('userId', userId.toString());
    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.pageSize) params.append('pageSize', pagination.pageSize.toString());
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<PagedResult<ApiWorkEntry>>(`/workentries${query}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  // Legacy method for backward compatibility
  async getAllWorkEntries(date?: string, userId?: number): Promise<ApiWorkEntry[]> {
    const result = await this.getWorkEntries(date, userId, { page: 1, pageSize: 1000 });
    return result.data;
  }

  async getWorkEntry(id: number): Promise<ApiWorkEntry> {
    return this.request<ApiWorkEntry>(`/workentries/${id}`);
  }

  async getActiveWorkEntry(userId: number, bypassCache: boolean = true): Promise<ApiWorkEntry | null> {
    // Check if optimized endpoints are available
    const useOptimized = await this.checkOptimizedEndpointsAvailable();
    
    if (useOptimized) {
      try {
        // Try optimized endpoint with cache invalidation support
        const timestamp = bypassCache ? Date.now() : '';
        const url = `/optimized/workentries/active/${userId}${timestamp ? `?_t=${timestamp}` : ''}`;
        
        const result = await this.request<ApiWorkEntry | null>(url, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        return result;
      } catch (error: unknown) {
        // If optimized endpoint fails for any reason, fall back to regular
        console.log(`Optimized endpoint failed for user ${userId}, using fallback`);
      }
    }
    
    // Use regular endpoint (either because optimized is not available or failed)
    try {
      // Get today's work entries and find active one
      const today = new Date().toISOString().split('T')[0];
      const entriesResponse = await this.getWorkEntries(today, userId);
      const entries = Array.isArray(entriesResponse) ? entriesResponse : (entriesResponse.data || []);
      
      // Find active entry (no endTime)
      const activeEntry = entries.find((entry: ApiWorkEntry) => !entry.endTime);
      return activeEntry || null;
    } catch (fallbackError) {
      console.error(`Failed to get active work entry for user ${userId}:`, fallbackError);
      return null;
    }
  }

  // Bulk operations for better performance - Real-time data (no caching)
  async getBulkWorkEntries(userIds: number[], date: string): Promise<BulkWorkEntriesResult> {
    const params = new URLSearchParams();
    userIds.forEach(id => params.append('userIds', id.toString()));
    params.append('date', date);
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<BulkWorkEntriesResult>(`/workentries/bulk${query}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  async createWorkEntry(data: CreateWorkEntryRequest): Promise<ApiWorkEntry> {
    return this.request<ApiWorkEntry>('/workentries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWorkEntry(id: number, data: UpdateWorkEntryRequest): Promise<void> {
    return this.request<void>(`/workentries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async addComment(workEntryId: number, comment: string): Promise<void> {
    return this.request<void>(`/workentries/${workEntryId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    });
  }

  async deleteWorkEntry(id: number): Promise<void> {
    return this.request<void>(`/workentries/${id}`, {
      method: 'DELETE',
    });
  }

  // Breaks
  async getBreaksByWorkEntry(workEntryId: number): Promise<ApiBreak[]> {
    return this.request<ApiBreak[]>(`/breaks/workentry/${workEntryId}`);
  }

  async createBreak(data: CreateBreakRequest): Promise<ApiBreak> {
    return this.request<ApiBreak>('/breaks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBreak(id: number, data: UpdateBreakRequest): Promise<void> {
    return this.request<void>(`/breaks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteBreak(id: number): Promise<void> {
    return this.request<void>(`/breaks/${id}`, {
      method: 'DELETE',
    });
  }

  // Teams - Real-time data (no caching for live updates)
  async getTeams(): Promise<ApiTeam[]> {
    return this.request<ApiTeam[]>('/teams', {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  async getTeamById(id: number): Promise<ApiTeam> {
    return this.request<ApiTeam>(`/teams/${id}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  async getTeamByUserId(userId: number): Promise<ApiTeam | null> {
    return this.request<ApiTeam | null>(`/teams/user/${userId}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  async createTeam(data: CreateTeamRequest): Promise<ApiTeam> {
    return this.request<ApiTeam>('/teams', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTeam(id: number, data: UpdateTeamRequest): Promise<void> {
    return this.request<void>(`/teams/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTeam(id: number): Promise<void> {
    return this.request<void>(`/teams/${id}`, {
      method: 'DELETE',
    });
  }

  async softDeleteTeam(id: number): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(`/teams/${id}/soft-delete`, {
      method: 'PUT',
    });
  }

  async addUserToTeam(teamId: number, userId: number): Promise<void> {
    return this.request<void>(`/teams/${teamId}/users/${userId}`, {
      method: 'POST',
    });
  }

  async removeUserFromTeam(teamId: number, userId: number): Promise<void> {
    return this.request<void>(`/teams/${teamId}/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Optimized endpoints for large teams
  async getWorkEntriesByUserIds(userIds: number[], date: string): Promise<ApiWorkEntry[]> {
    return this.request<ApiWorkEntry[]>('/optimized/supervisor/get-work-entries-by-users', {
      method: 'POST',
      body: JSON.stringify({ userIds, date })
    });
  }

  async getTeamMembersPaginated(supervisorId: number, page: number = 1, pageSize: number = 50, search?: string, status?: string): Promise<{
    items: ApiUser[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    
    return this.request(`/optimized/supervisor/team-members-paginated/${supervisorId}?${params}`, {
      headers: {
        'Cache-Control': 'no-cache'
      }
    });
  }

  async bulkStartWorkOptimized(userIds: number[], projectId: number, locationId: number): Promise<BulkOperationResult> {
    return this.request<BulkOperationResult>('/optimized/supervisor/bulk-start-work-optimized', {
      method: 'POST',
      body: JSON.stringify({ userIds, projectId, locationId })
    });
  }

  async bulkStopWorkOptimized(userIds: number[], comments?: string): Promise<BulkOperationResult> {
    return this.request<BulkOperationResult>('/optimized/supervisor/bulk-stop-work-optimized', {
      method: 'POST',
      body: JSON.stringify({ userIds, comments })
    });
  }

  // Worker Status - Optimized lightweight endpoints
  async getWorkerStatus(userId: number): Promise<WorkerStatus> {
    return this.request<WorkerStatus>(`/workerstatus/status/${userId}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  async getBulkWorkerStatus(userIds: number[]): Promise<WorkerStatus[]> {
    return this.request<WorkerStatus[]>('/workerstatus/bulk-status', {
      method: 'POST',
      body: JSON.stringify(userIds),
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  async startWorkOptimized(userId: number, projectId: number, locationId: number): Promise<WorkerStatus> {
    return this.request<WorkerStatus>(`/workerstatus/start/${userId}`, {
      method: 'POST',
      body: JSON.stringify({ projectId, locationId })
    });
  }

  async stopWorkOptimized(userId: number, comments?: string): Promise<WorkerStatus> {
    return this.request<WorkerStatus>(`/workerstatus/stop/${userId}`, {
      method: 'POST',
      body: JSON.stringify({ comments })
    });
  }

  async supervisorBulkStart(userIds: number[], projectId: number, locationId: number): Promise<BulkStatusResult> {
    return this.request<BulkStatusResult>('/workerstatus/supervisor/bulk-start', {
      method: 'POST',
      body: JSON.stringify({ userIds, projectId, locationId })
    });
  }

  // Supervisor actions - Real-time data (no caching for live updates)
  async getTeamMembers(supervisorId: number): Promise<ApiUser[]> {
    return this.request<ApiUser[]>(`/users/supervisor/${supervisorId}/team`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  // Sync user status with work entries to fix inconsistencies
  async syncUserStatus(): Promise<{ message: string; correctedCount: number; skippedCount?: number }> {
    return this.request<{ message: string; correctedCount: number; skippedCount?: number }>('/users/sync-status', {
      method: 'POST',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });
  }

  async simpleDebug(): Promise<unknown> {
    return this.request<unknown>(`/legacy/workentries/simple-debug`, {
      method: 'GET',
    });
  }

  async debugSupervisorStart(userId: number, data: CreateWorkEntryRequest): Promise<unknown> {
    return this.request<unknown>(`/legacy/workentries/supervisor-debug/${userId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async startWorkForUser(userId: number, data: CreateWorkEntryRequest): Promise<ApiWorkEntry> {
    return this.request<ApiWorkEntry>('/supervisor/start-work', {
      method: 'POST',
      body: JSON.stringify({
        userId: userId,
        projectId: data.projectId,
        locationId: data.locationId
      }),
    });
  }

  async endWorkForUser(workEntryId: number, data: UpdateWorkEntryRequest): Promise<void> {
    return this.request<void>(`/supervisor/stop-work/${workEntryId}`, {
      method: 'POST',
      body: JSON.stringify({
        comments: data.comments
      }),
    });
  }

  async bulkStartWork(userIds: number[], projectId: number, locationId: number): Promise<BulkOperationResult> {
    const backendResult = await this.request<BackendBulkOperationResult>('/supervisor/bulk-start-work-fast', {
      method: 'POST',
      body: JSON.stringify({
        userIds,
        projectId,
        locationId
      }),
    });
    
    console.log('Raw backend response for bulkStartWork:', backendResult);
    
    // Additional safety check
    if (!backendResult || typeof backendResult !== 'object') {
      console.error('Backend result is null, undefined, or not an object:', backendResult);
      throw new Error('No response from server');
    }
    
    // Handle both PascalCase and lowercase property names from backend
    const success = backendResult.Success ?? backendResult.success ?? false;
    const successCount = backendResult.SuccessCount ?? backendResult.successCount ?? 0;
    const errorCount = backendResult.ErrorCount ?? backendResult.errorCount ?? 0;
    const message = backendResult.Message ?? backendResult.message ?? 'No message';
    const resultsArray = backendResult.Results ?? backendResult.results ?? [];
    
    // Ensure results is an array before mapping
    const results = Array.isArray(resultsArray) ? resultsArray : [];
    
    // Transform backend response to frontend interface
    return {
      success,
      successCount,
      errorCount,
      message,
      results: results.map(r => ({
        userId: r?.UserId ?? r?.userId ?? 0,
        workEntryId: r?.WorkEntryId ?? r?.workEntryId ?? 0,
        success: r?.Success ?? r?.success ?? false,
        error: r?.Error ?? r?.error ?? 'Unknown error'
      }))
    };
  }

  async bulkStopWork(workEntryIds: number[], comments?: string): Promise<BulkOperationResult> {
    const backendResult = await this.request<BackendBulkOperationResult>('/supervisor/bulk-stop-work-fast', {
      method: 'POST',
      body: JSON.stringify({
        workEntryIds,
        comments
      }),
    });
    
    console.log('Raw backend response for bulkStopWork:', backendResult);
    
    // Additional safety check
    if (!backendResult || typeof backendResult !== 'object') {
      console.error('Backend result is null, undefined, or not an object:', backendResult);
      throw new Error('No response from server');
    }
    
    // Handle both PascalCase and lowercase property names from backend
    const success = backendResult.Success ?? backendResult.success ?? false;
    const successCount = backendResult.SuccessCount ?? backendResult.successCount ?? 0;
    const errorCount = backendResult.ErrorCount ?? backendResult.errorCount ?? 0;
    const message = backendResult.Message ?? backendResult.message ?? 'No message';
    const resultsArray = backendResult.Results ?? backendResult.results ?? [];
    
    // Ensure results is an array before mapping
    const results = Array.isArray(resultsArray) ? resultsArray : [];
    
    // Transform backend response to frontend interface
    return {
      success,
      successCount,
      errorCount,
      message,
      results: results.map(r => ({
        userId: r?.UserId ?? r?.userId ?? 0,
        workEntryId: r?.WorkEntryId ?? r?.workEntryId ?? 0,
        success: r?.Success ?? r?.success ?? false,
        error: r?.Error ?? r?.error ?? 'Unknown error'
      }))
    };
  }

  async startBreakForUser(workEntryId: number, data: CreateBreakRequest): Promise<ApiBreak> {
    return this.request<ApiBreak>(`/breaks`, {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        workEntryId
      }),
    });
  }

  async endBreakForUser(breakId: number, data: UpdateBreakRequest): Promise<void> {
    return this.request<void>(`/breaks/${breakId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Table actions - Similar to supervisor actions but for table interface
  async startWorkForUserFromTable(userId: number, data: CreateWorkEntryRequest): Promise<ApiWorkEntry> {
    return this.request<ApiWorkEntry>(`/workentries`, {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        userId
      }),
    });
  }

  async endWorkForUserFromTable(workEntryId: number, data: UpdateWorkEntryRequest): Promise<void> {
    return this.request<void>(`/workentries/${workEntryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Supervisor actions - Get teams for supervisor
  async getSupervisorTeams(): Promise<ApiTeam[]> {
    // Ensure we have the current user ID
    if (!this.currentUserId) {
      console.error('getSupervisorTeams called without currentUserId set');
      return [];
    }
    
    return this.request<ApiTeam[]>('/supervisor/my-teams', {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
        'X-Current-User-Id': this.currentUserId.toString()
      }
    });
  }

  // Monitor actions - Real-time data (no caching for live updates)
  async getMonitoredTeams(monitorId: number): Promise<ApiTeam[]> {
    return this.request<ApiTeam[]>(`/teams/monitor/${monitorId}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  async getSupervisorsByMonitor(monitorId: number): Promise<ApiUser[]> {
    return this.request<ApiUser[]>(`/users/monitor/${monitorId}/supervisors`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  // Material Management APIs - Real-time data (no caching)
  async getMaterialTypes(): Promise<MaterialType[]> {
    return this.request<MaterialType[]>('/materialtypes', {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  async createMaterialType(data: { name: string; defaultUnit: string; category?: string }): Promise<MaterialType> {
    return this.request<MaterialType>('/materialtypes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMaterialType(id: number, data: { name?: string; defaultUnit?: string; category?: string }): Promise<void> {
    return this.request<void>(`/materialtypes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteMaterialType(id: number): Promise<void> {
    return this.request<void>(`/materialtypes/${id}`, {
      method: 'DELETE',
    });
  }

  async getMaterialUnitsForType(materialTypeId: number): Promise<MaterialUnit[]> {
    return this.request<unknown[]>(`/materialtypes/${materialTypeId}/units`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  // Note: Material units are managed through MaterialTypesController
  // These endpoints might not exist as separate CRUD operations in the backend
  async createMaterialUnit(data: { name: string; symbol?: string; materialTypeId: number }): Promise<MaterialUnit> {
    // This might need to be implemented in the backend or use a different approach
    throw new Error('Material unit creation not implemented - units are managed through MaterialTypesController');
  }

  async updateMaterialUnit(id: number, data: { name?: string; symbol?: string }): Promise<void> {
    // This might need to be implemented in the backend or use a different approach
    throw new Error('Material unit update not implemented - units are managed through MaterialTypesController');
  }

  async deleteMaterialUnit(id: number): Promise<void> {
    // This might need to be implemented in the backend or use a different approach
    throw new Error('Material unit deletion not implemented - units are managed through MaterialTypesController');
  }

  async getMaterialConsumptions(workEntryId?: number, date?: string): Promise<MaterialConsumption[]> {
    if (workEntryId) {
      return this.request<MaterialConsumption[]>(`/materialconsumptions/workentry/${workEntryId}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
    }
    // If no workEntryId provided, return empty array
    return [];
  }

  async createMaterialConsumption(data: { 
    workEntryId: number;
    materialTypeId: number;
    quantity: number;
    unitId: number;
    addedByUserId: number;
    addedForUserId: number;
    notes?: string;
  }): Promise<MaterialConsumption> {
    return this.request<MaterialConsumption>('/materialconsumptions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMaterialConsumption(id: number, data: { 
    quantity?: number;
    unitId?: number;
    notes?: string;
  }): Promise<void> {
    return this.request<void>(`/materialconsumptions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteMaterialConsumption(id: number): Promise<void> {
    return this.request<void>(`/materialconsumptions/${id}`, {
      method: 'DELETE',
    });
  }

  async getMaterialAllocations(projectId?: number): Promise<MaterialAllocation[]> {
    if (projectId) {
      return this.request<MaterialAllocation[]>(`/materialallocations/project/${projectId}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
    }
    return [];
  }

  async createMaterialAllocation(data: {
    projectId: number;
    materialTypeId: number;
    allocatedQuantity: number;
    unitId: number;
    allocationDate: string;
    notes?: string;
  }): Promise<MaterialAllocation> {
    return this.request<MaterialAllocation>('/materialallocations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMaterialAllocation(id: number, data: {
    allocatedQuantity?: number;
    unitId?: number;
    notes?: string;
  }): Promise<void> {
    return this.request<void>(`/materialallocations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteMaterialAllocation(id: number): Promise<void> {
    return this.request<void>(`/materialallocations/${id}`, {
      method: 'DELETE',
    });
  }

  async getMaterialWasteReport(projectId?: number): Promise<MaterialWasteReport[]> {
    if (projectId) {
      return this.request<MaterialWasteReport[]>(`/materialallocations/waste-report/${projectId}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
    }
    return [];
  }

  // Action Log APIs - Real-time data (no caching)
  async getActionLogs(entityType?: string, entityId?: number, userId?: number): Promise<ActionLog[]> {
    const params = new URLSearchParams();
    if (entityType) params.append('entityType', entityType);
    if (entityId) params.append('entityId', entityId.toString());
    if (userId) params.append('userId', userId.toString());
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<ActionLog[]>(`/auditlogs${query}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  }

  // End-of-day report API
  async generateEndOfDayReport(date: string, endTime?: string): Promise<EndOfDayReportResult> {
    return this.request<EndOfDayReportResult>('/reports/end-of-day', {
      method: 'POST',
      body: JSON.stringify({
        date: date,
        endTime: endTime
      }),
    });
  }

  // Supervisor End-of-Day Reports
  async createSupervisorEndOfDayReport(date: string, endTime?: string, notes?: string, teamId?: number): Promise<SupervisorEndOfDayReportResult> {
    return this.request<SupervisorEndOfDayReportResult>('/reports/supervisor-end-of-day', {
      method: 'POST',
      body: JSON.stringify({
        date: date,
        endTime: endTime,
        notes: notes,
        teamId: teamId
      }),
    });
  }

  async getEndOfDayReportsForMonitor(startDate?: string, endDate?: string): Promise<EndOfDayReportSummary[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    return this.request<EndOfDayReportSummary[]>(`/reports/monitor-end-of-day-reports?${params.toString()}`);
  }

  async downloadEndOfDayReport(reportId: number): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/reports/download-end-of-day-report/${reportId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download report: ${response.statusText}`);
    }

    return response.blob();
  }

  // Admin APIs
  async adminBulkStopWork(workEntryIds: number[], comments?: string): Promise<{
    totalRequested: number;
    successful: number[];
    failed: Array<{ workEntryId: number; userName: string; reason: string }>;
    message: string;
  }> {
    return this.request('/admin/bulk-stop-work-fast', {
      method: 'POST',
      body: JSON.stringify({ workEntryIds, comments }),
    });
  }

  // User work settings methods
  async getUserWorkSettings(userId: number): Promise<{
    userId: number;
    userName: string;
    dailyBreakMinutes: number;
    dailyWorkQuotaMinutes: number;
    dailyBreakHours: number;
    dailyWorkQuotaHours: number;
  }> {
    return this.request(`/users/${userId}/work-settings`);
  }

  async updateUserWorkSettings(userId: number, settings: {
    dailyWorkQuotaMinutes: number;
    dailyBreakQuotaMinutes: number;
  }): Promise<{
    userId: number;
    userName: string;
    dailyBreakMinutes: number;
    dailyWorkQuotaMinutes: number;
  }> {
    return this.request(`/users/${userId}/work-settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // Overtime API methods
  async getOvertimes(params?: {
    date?: string;
    userId?: number;
    page?: number;
    pageSize?: number;
  }): Promise<{
    overtimes: any[];
    totalCount: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.date) queryParams.append('date', params.date);
    if (params?.userId) queryParams.append('userId', params.userId.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    
    return this.request(`/overtimes?${queryParams.toString()}`);
  }

  async getPendingOvertimeApprovals(): Promise<{
    pendingCount: number;
    overtimes: any[];
  }> {
    return this.request('/overtimes/pending-approval');
  }

  async approveOvertime(overtimeId: number, data: {
    isApproved: boolean;
    approvedById: number;
    comment?: string;
  }): Promise<{
    message: string;
    overtimeId: number;
    isApproved: boolean;
  }> {
    return this.request(`/overtimes/${overtimeId}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getApprovedOvertime(params?: {
    startDate?: string;
    endDate?: string;
    teamId?: number;
    userId?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.teamId) queryParams.append('teamId', params.teamId.toString());
    if (params?.userId) queryParams.append('userId', params.userId.toString());
    
    return this.request(`/overtimes/approved?${queryParams.toString()}`);
  }

  async calculateDailyOvertime(date?: Date): Promise<{
    date: string;
    recordsProcessed: number;
    overtimeRecords: any[];
  }> {
    return this.request('/overtimes/calculate-daily', {
      method: 'POST',
      body: JSON.stringify({ date: date?.toISOString() }),
    });
  }

  async calculateOvertimeRange(params: {
    startDate?: Date;
    endDate?: Date;
    skipExisting?: boolean;
  }): Promise<{
    startDate: string;
    endDate: string;
    daysProcessed: number;
    totalRecordsProcessed: number;
    newOvertimeRecordsCreated: number;
    dailyBreakdown: any[];
  }> {
    return this.request('/overtimes/calculate-range', {
      method: 'POST',
      body: JSON.stringify({
        startDate: params.startDate?.toISOString(),
        endDate: params.endDate?.toISOString(),
        skipExisting: params.skipExisting || false,
      }),
    });
  }

  // Activities API
  async getActivities(params?: {
    date?: string;
    userId?: number;
    teamId?: number;
    page?: number;
    pageSize?: number;
  }): Promise<{
    activities: any[];
    totalCount: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.date) queryParams.append('date', params.date);
    if (params?.userId) queryParams.append('userId', params.userId.toString());
    if (params?.teamId) queryParams.append('teamId', params.teamId.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    
    const url = queryParams.toString() ? `/activities?${queryParams}` : '/activities';
    return this.request(url);
  }

  async createActivity(activity: {
    name: string;
    quantity: number;
    unit: string;
    userId: number;
    teamId?: number;
    date?: Date;
    projectId?: number;
    locationId?: number;
    notes?: string;
    createdById: number;
    endOfDayReportId?: number;
  }): Promise<any> {
    return this.request('/activities', {
      method: 'POST',
      body: JSON.stringify({
        ...activity,
        date: activity.date?.toISOString()
      }),
    });
  }

  async getActivitiesByDateRange(params?: {
    startDate?: string;
    endDate?: string;
    teamId?: number;
    projectId?: number;
    supervisorId?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.teamId) queryParams.append('teamId', params.teamId.toString());
    if (params?.projectId) queryParams.append('projectId', params.projectId.toString());
    if (params?.supervisorId) queryParams.append('supervisorId', params.supervisorId.toString());
    
    return this.request(`/activities/range?${queryParams.toString()}`);
  }

  async createBulkActivities(activities: any[]): Promise<any> {
    return this.request('/activities/bulk', {
      method: 'POST',
      body: JSON.stringify({ activities }),
    });
  }

  async getMaterialConsumptionsByDateRange(params?: {
    startDate?: string;
    endDate?: string;
    teamId?: number;
    projectId?: number;
    supervisorId?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.teamId) queryParams.append('teamId', params.teamId.toString());
    if (params?.projectId) queryParams.append('projectId', params.projectId.toString());
    if (params?.supervisorId) queryParams.append('supervisorId', params.supervisorId.toString());
    
    return this.request(`/materialconsumptions/range?${queryParams.toString()}`);
  }

  async updateActivity(id: number, updates: {
    name?: string;
    quantity?: number;
    unit?: string;
    notes?: string;
  }): Promise<any> {
    return this.request(`/activities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteActivity(id: number): Promise<any> {
    return this.request(`/activities/${id}`, {
      method: 'DELETE',
    });
  }

  async getActivitySummary(params?: {
    startDate?: string;
    endDate?: string;
    teamId?: number;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.teamId) queryParams.append('teamId', params.teamId.toString());
    
    const url = queryParams.toString() ? `/activities/summary?${queryParams}` : '/activities/summary';
    return this.request(url);
  }


  async getOvertimeSummary(date?: string): Promise<{
    date: string;
    totalOvertimeHours: number;
    workersWithOvertime: number;
    autoGeneratedCount: number;
    pendingApprovalCount: number;
    topWorkers: any[];
  }> {
    const queryParams = date ? `?date=${date}` : '';
    return this.request(`/overtimes/summary${queryParams}`);
  }
}

export const apiService = new ApiService();